import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { WorldPos } from '../../utils/measureUtils';

export interface SceneLabelItem {
  id: string;
  text: string;
  worldPos: WorldPos;
  gps?: [number, number];
}

interface SceneLabelsLayerProps {
  viewerRef: React.MutableRefObject<any>;
  labels: SceneLabelItem[];
  interactive?: boolean;
  selectedId?: string | null;
  draggingId?: string | null;
  maxDistance?: number;
  showHiddenSummary?: boolean;
  formatHiddenSummary?: (count: number, lastText: string) => string;
  onLabelPointerDown?: (label: SceneLabelItem, event: React.PointerEvent<HTMLDivElement>) => void;
}

const SceneLabelsLayer: React.FC<SceneLabelsLayerProps> = ({
  viewerRef,
  labels,
  interactive = false,
  selectedId = null,
  draggingId = null,
  maxDistance = 40,
  showHiddenSummary = false,
  formatHiddenSummary,
  onLabelPointerDown,
}) => {
  const rafRef = useRef<number | null>(null);
  const labelDivRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const labelsRef = useRef(labels);
  const formatHiddenSummaryRef = useRef(formatHiddenSummary);
  const projectionVec = useRef(new THREE.Vector3());
  const distanceVec = useRef(new THREE.Vector3());

  useEffect(() => { labelsRef.current = labels; }, [labels]);
  useEffect(() => { formatHiddenSummaryRef.current = formatHiddenSummary; }, [formatHiddenSummary]);

  // Update selected/dragging styles imperatively without re-render
  useEffect(() => {
    for (const [id, el] of labelDivRefs.current) {
      const pill = el.querySelector('[data-pill]') as HTMLElement | null;
      if (!pill) continue;
      const isSelected = id === selectedId;
      pill.style.background = isSelected ? 'rgba(253, 186, 89, 0.95)' : 'rgba(0,0,0,0.8)';
      pill.style.borderColor = isSelected ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.2)';
      pill.style.color = isSelected ? '#000' : '#fff';
      el.style.cursor = interactive
        ? (draggingId === id ? 'grabbing' : 'grab')
        : 'default';
    }
  }, [selectedId, draggingId, interactive]);

  // RAF loop — updates transforms directly, never touches React state
  useEffect(() => {
    if (labels.length === 0) return;

    const tick = () => {
      const viewer = viewerRef.current;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      const camera = viewer?.camera as (THREE.Camera & { position: THREE.Vector3 }) | undefined;

      if (canvas && camera) {
        const rect = canvas.getBoundingClientRect();
        let hiddenCount = 0;
        let lastHiddenText = '';

        for (const label of labelsRef.current) {
          const el = labelDivRefs.current.get(label.id);
          if (!el) continue;

          projectionVec.current.set(label.worldPos.x, label.worldPos.y, label.worldPos.z);
          projectionVec.current.project(camera);

          const inViewport =
            projectionVec.current.x >= -1 && projectionVec.current.x <= 1 &&
            projectionVec.current.y >= -1 && projectionVec.current.y <= 1 &&
            projectionVec.current.z >= -1 && projectionVec.current.z <= 1;

          if (!inViewport) {
            el.style.display = 'none';
            hiddenCount++;
            lastHiddenText = label.text;
            continue;
          }

          distanceVec.current.set(label.worldPos.x, label.worldPos.y, label.worldPos.z);
          const distance = camera.position.distanceTo(distanceVec.current);

          if (distance > maxDistance) {
            el.style.display = 'none';
            hiddenCount++;
            lastHiddenText = label.text;
            continue;
          }

          const x = Math.round(rect.left + (projectionVec.current.x * 0.5 + 0.5) * rect.width);
          const y = Math.round(rect.top + (-projectionVec.current.y * 0.5 + 0.5) * rect.height);
          const scale = Math.max(0.85, Math.min(1.15, 10 / Math.max(distance, 1)));

          el.style.display = 'block';
          el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -100%) scale(${scale})`;
        }

        if (summaryRef.current && showHiddenSummary) {
          if (hiddenCount > 0) {
            summaryRef.current.style.display = 'block';
            const fmt = formatHiddenSummaryRef.current;
            summaryRef.current.textContent = fmt
              ? fmt(hiddenCount, lastHiddenText)
              : hiddenCount === 1
                ? `${lastHiddenText} label is outside the current view.`
                : `${hiddenCount} labels are outside the current view.`;
          } else {
            summaryRef.current.style.display = 'none';
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [labels, maxDistance, viewerRef, showHiddenSummary]);

  if (labels.length === 0) return null;

  return (
    <>
      {labels.map((label) => (
        <div
          key={label.id}
          ref={(el) => {
            if (el) labelDivRefs.current.set(label.id, el);
            else labelDivRefs.current.delete(label.id);
          }}
          className="fixed z-[1002] select-none"
          style={{
            left: 0,
            top: 0,
            display: 'none',
            willChange: 'transform',
            transformOrigin: 'bottom center',
            pointerEvents: interactive ? 'auto' : 'none',
            cursor: interactive ? 'grab' : 'default',
          }}
          onPointerDown={(event) => {
            if (!interactive) return;
            onLabelPointerDown?.(label, event);
          }}
        >
          <div
            data-pill="true"
            className="rounded-md border px-2 py-1 text-xs font-semibold shadow-lg"
            style={{
              background: selectedId === label.id ? 'rgba(253, 186, 89, 0.95)' : 'rgba(0,0,0,0.8)',
              borderColor: selectedId === label.id ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.2)',
              color: selectedId === label.id ? '#000' : '#fff',
            }}
          >
            {label.text.includes('\n') ? (() => {
              const [name, ...rest] = label.text.split('\n');
              return (
                <>
                  {name}
                  {rest.map((line, i) => (
                    <div key={i} style={{ fontWeight: 400, fontSize: '0.6rem', opacity: 0.85, marginTop: 1 }}>{line}</div>
                  ))}
                </>
              );
            })() : label.text}
          </div>
          <div className="mx-auto h-3 w-px bg-white/60" />
          <div className="mx-auto h-2 w-2 rounded-full border border-white bg-[#0CB4E4]" />
        </div>
      ))}

      {showHiddenSummary && (
        <div
          ref={summaryRef}
          className="fixed bottom-4 left-4 z-[1002] max-w-xs rounded-md border border-white/15 bg-black/80 px-3 py-2 text-xs text-white/80 shadow-lg"
          style={{ display: 'none' }}
        />
      )}
    </>
  );
};

export default SceneLabelsLayer;
